import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { Badge, notConfiguredText } from '@wit-support/ui';
import { ModuleScreen } from '@wit-support/ui-domain';
import { CircleDashed, Lock, PencilLine } from 'lucide-react';
import { AccountingRequestError, getAccountingHealth, getAccountingRegions } from './api.js';
/** Корень всех ключей раздела — одна инвалидация после мутации обновляет все экраны. */
export const ACCOUNTING_KEY = ['accounting'];
export const HEALTH_KEY = ['accounting', 'health'];
export const REGIONS_KEY = ['accounting', 'regions'];
export const quartersKey = ['accounting', 'quarters'];
export const quarterKey = (quarter) => ['accounting', 'quarter', quarter];
const SERVICE = 'Платёжный сервис (бухгалтерия)';
const ENV = ['ACCOUNTING_API_BASE_URL', 'ACCOUNTING_API_TOKEN'];
export const NOT_CONFIGURED_TEXT = notConfiguredText(SERVICE, ENV);
export const UNAVAILABLE_TEXT = 'Платёжный сервис недоступен, попробуйте позже';
export const FORBIDDEN_READ_TEXT = 'Раздел «Бухгалтерия» недоступен вашей роли: его видят супервайзеры и владелец.';
export const FORBIDDEN_WRITE_TEXT = 'Пересчитывать кварталы и отмечать подачу могут только супервайзер и владелец.';
/**
 * Ошибку — словами. `mode: 'write'` — для мутаций: 403 там значит «смотреть
 * можно, менять нельзя». Отказ платёжки (`upstream_rejected`: «квартал уже
 * подан», «квартал ещё не закончился») admin-api присылает русской фразой в
 * `message` — она и показывается.
 */
export function describeAccountingError(err, mode = 'read') {
    const prefix = mode === 'write' ? 'Не удалось выполнить' : 'Не удалось загрузить';
    if (err instanceof AccountingRequestError) {
        if (err.status === 403)
            return mode === 'write' ? FORBIDDEN_WRITE_TEXT : FORBIDDEN_READ_TEXT;
        if (err.code === 'accounting_not_configured')
            return NOT_CONFIGURED_TEXT;
        if (err.code === 'upstream_rejected')
            return err.message;
        if (err.status === 502 || err.status === 503 || err.status === 504)
            return UNAVAILABLE_TEXT;
        return `${prefix}: ${err.message}`;
    }
    return `${prefix}: ${err instanceof Error ? err.message : String(err)}`;
}
/**
 * Сабмит формы через `mutateAsync`: `ZodForm` ждёт промис, поэтому кнопка
 * заблокирована на время запроса. Ошибку уже показал `onError` мутации.
 */
export async function submitting(p) {
    await p.catch(() => undefined);
}
/**
 * Итог пересчёта словами: «Пересчитано: ЕС — OSS. Не тронуты (уже поданы):
 * Великобритания».
 */
export function recalcToastText(results, labelOf) {
    const done = results.filter((r) => r.outcome === 'recalculated').map((r) => labelOf(r.region));
    const skipped = results
        .filter((r) => r.outcome === 'skipped_filed')
        .map((r) => labelOf(r.region));
    const parts = [];
    parts.push(done.length > 0 ? `Пересчитано: ${done.join(', ')}` : 'Ничего не пересчитано');
    if (skipped.length > 0)
        parts.push(`не тронуты (уже поданы): ${skipped.join(', ')}`);
    return parts.join('; ');
}
export function useAccountingHealth() {
    return useQuery({ queryKey: HEALTH_KEY, queryFn: getAccountingHealth });
}
/**
 * Названия регионов по id (`eu-oss` → «ЕС — OSS (ROS, Ирландия)»). Сводка
 * кварталов несёт только id; пока справочник не пришёл (или упал) — id.
 */
export function useRegionLabels() {
    const q = useQuery({ queryKey: REGIONS_KEY, queryFn: getAccountingRegions });
    const labels = new Map((q.data?.regions ?? []).map((r) => [r.id, r.label]));
    return (region) => labels.get(region) ?? region;
}
export const REGION_STATUS_LABEL_RU = {
    draft: 'Черновик',
    filed: 'Подан',
    not_computed: 'Не посчитан',
};
/** Статус региона за квартал: «Черновик» / «Подан» (замок) / «Не посчитан». */
export function RegionStatusBadge({ status }) {
    const Icon = status === 'filed' ? Lock : status === 'draft' ? PencilLine : CircleDashed;
    const variant = status === 'filed' ? 'success' : status === 'draft' ? 'info' : 'neutral';
    return (_jsxs(Badge, { variant: variant, children: [_jsx(Icon, { "aria-hidden": true, className: "h-3 w-3" }), REGION_STATUS_LABEL_RU[status]] }));
}
/**
 * Каркас экрана раздела (`ModuleScreen` из ui-domain): заголовок + проверка
 * «платёжка подключена». Тело монтируется только при `configured: true` —
 * иначе каждый экран показал бы свою ошибку 503 вместо одной фразы, что
 * задать в env.
 */
export function AccountingScreen({ title, description, actions, children }) {
    const health = useAccountingHealth();
    return (_jsx(ModuleScreen, { title: title, description: description, actions: actions, health: health, service: SERVICE, envVars: ENV, describeError: describeAccountingError, unavailableText: UNAVAILABLE_TEXT, children: children }));
}
//# sourceMappingURL=shared.js.map